import { useEffect } from 'react'

const ImplementationsQuestion = ({ formData, setFormData, questionState, setQuestionState, validationErrors = [] }) => {

    useEffect(() => {
        const existingResponses = formData.form_responses?.question4?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(response => {
                if (newState[response.answer_text]) {
                    newState[response.answer_text].selected = true;

                    if (response.text_response) {
                        newState[response.answer_text].text_response = response.text_response;
                    }
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question4?.responses])

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null) => {
        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                newState[responseKey] = {
                    ...newState[responseKey],
                    [dataKey]: value
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };

                if (checkedValue) {
                    if (responseKey === 'None') {
                        Object.keys(newState)
                            .filter(key => key !== 'None')
                            .forEach(key => {
                                newState[key] = { ...newState[key], selected: false };
                            });
                    } else {
                        newState.None = { ...newState.None, selected: false };
                    }
                }
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question4?.responses || [];

            if (dataKey) {
                const updatedResponses = currentResponses.map(response =>
                    response.answer_text === responseKey
                        ? { ...response, [dataKey]: value }
                        : response
                );

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question4: {
                            question_title: "What actions have been implemented?",
                            responses: updatedResponses
                        }
                    }
                };
            } else {
                let updatedResponses = [...currentResponses];

                if (checkedValue) {
                    updatedResponses = updatedResponses.filter(response => (
                        responseKey === 'None'
                            ? response.answer_text === 'None'
                            : response.answer_text !== 'None'
                    ));

                    if (!updatedResponses.find(r => r.answer_text === responseKey)) {
                        updatedResponses.push({
                            answer_text: responseKey,
                            sub_responses: []
                        });
                    }
                } else {
                    updatedResponses = updatedResponses.filter(r => r.answer_text !== responseKey);
                }

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question4: {
                            question_title: "What actions have been implemented?",
                            responses: updatedResponses
                        }
                    }
                };
            }
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey) => {
        return questionState[responseKey]?.[dataKey] || '';
    };
    const isNoneSelected = hasResponse('None');
    const hasOtherResponse = Object.keys(questionState)
        .filter(key => key !== 'None')
        .some(key => hasResponse(key));

    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 5: What actions have been implemented?
                </h2>
                <span className="text-red-500 text-sm font-medium">Required *</span>
            </div>
            <div>
                {validationErrors.includes('no_selection') && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-3 mx-4 mt-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    Please select at least one option to continue.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`${validationErrors.includes('no_selection') ? 'border-2 border-red-200 bg-red-50 m-2 rounded-md' : ''}`}>
                    <div className="p-2">
                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="temp_adjustments"
                                    value="Temporary Adjustments"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Temporary Adjustments', e.target.checked)}
                                    checked={hasResponse('Temporary Adjustments')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="temp_adjustments" className="text-sm text-gray-700">Temporary Adjustments</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="perm_adjustments"
                                    value="Permanent Adjustments"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Permanent Adjustments', e.target.checked)}
                                    checked={hasResponse('Permanent Adjustments')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="perm_adjustments" className="text-sm text-gray-700">Permanent Adjustments</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="reduced_hours"
                                    value="Reduced Hours"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Reduced Hours', e.target.checked)}
                                    checked={hasResponse('Reduced Hours')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="reduced_hours" className="text-sm text-gray-700">Reduced Hours</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="phased_return"
                                    value="Phased Return To Work"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Phased Return To Work', e.target.checked)}
                                    checked={hasResponse('Phased Return To Work')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="phased_return" className="text-sm text-gray-700">Phased Return To Work</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="redeployment"
                                    value="Redeployment"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Redeployment', e.target.checked)}
                                    checked={hasResponse('Redeployment')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="redeployment" className="text-sm text-gray-700">Redeployment</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="other_actions"
                                    value="Other"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Other', e.target.checked)}
                                    checked={hasResponse('Other')}
                                    disabled={isNoneSelected}
                                />
                                <label htmlFor="other_actions" className="text-sm text-gray-700">Other (Please State)</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="none"
                                    value="None"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('None', e.target.checked)}
                                    checked={hasResponse('None')}
                                    disabled={hasOtherResponse}
                                />
                                <label htmlFor="none" className="text-sm text-gray-700">None Yet / Not Applicable</label>
                            </div>
                        </div>
                        {hasResponse('Other') && (
                            <div className="mt-2 ml-6">
                                <textarea
                                    id="actionsOther"
                                    name="actionsOther"
                                    rows="2"
                                    className="border rounded-md py-2 px-4 w-full resize-vertical"
                                    placeholder="Please specify other actions..."
                                    onChange={(e) => handleQuestionUpdate('Other', null, null, 'text_response', e.target.value)}
                                    value={getResponseData('Other', 'text_response')}
                                ></textarea>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImplementationsQuestion;
